import { Button } from 'iglooform';
import { useEffect, FC } from 'react';
import { history, IRouteComponentProps, useLocation } from 'umi';
import { Spin, Row, Col, Space } from 'antd';
import { useIntl } from 'react-intl';
import { useRequest } from 'ahooks';
import PageFrame from '@/components/page-frame';
import { GetProductDetail, GetPlanCmsDetail, CreateQuote } from './service';
import { images } from '@/assets/images';
import { getSession, deletePhsicalDamageParams } from '@/utils/common';
import classnames from 'classnames';
import useMobile from '@/hooks/useMobile';
import numeral from 'numeral';
import useUpdateRemoteLangMessage from '@/hooks/useUpdateRemoteLangMessage';
import useHandleFormData from '@/hooks/useHandleFormData';
import * as SESSION_KEYS from '@/configs/session-storage-keys';
import useHandleQuoteBack from './hooks/useHandleQuoteBack';
import './index.less';

const getProductDetailAndPlanCmsDetail = (
  productType: string,
  productId: string,
  planKey: string,
) => {
  return Promise.all([
    GetProductDetail(productType, productId),
    GetPlanCmsDetail(productType, productId, planKey),
  ]);
};

const ProductDetail: FC<IRouteComponentProps> = ({ match }) => {
  const { params }: any = match;
  const isMobile = useMobile();

  const { create } = useHandleFormData();

  const { formatMessage } = useIntl();
  const { query }: any = useLocation();
  const {
    totalStep,
    nextActiveStep,
    planKey,
    coverageType,
    soiCategory,
    backTo,
  } = query;

  const productType = params.type;

  const readyVehicleInfo = getSession(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE);
  const stillNeedCache = getSession(SESSION_KEYS.STILL_NEED_CACHE);

  const promptElement = useHandleQuoteBack();

  const { run: createQuote } = useRequest(CreateQuote, {
    onSuccess: (res) => {
      sessionStorage.setItem(SESSION_KEYS.QUOTE_INFO, JSON.stringify(res));
      history.push({
        pathname: `/home/${productType}/quote`,
        query: {
          totalStep,
          currentActiveStep: nextActiveStep,
        },
        state: {
          from: 'product-list',
        },
      });
    },
    manual: true,
  });

  const {
    run: getDetail,
    loading,
    data,
  } = useRequest<any, any>(getProductDetailAndPlanCmsDetail, {
    manual: true,
  });

  useUpdateRemoteLangMessage([
    () => getDetail(productType, params.productId, planKey),
  ]);

  useEffect(() => {
    getDetail(productType, params.productId, planKey);

    //event tracking: view product detail
    window.gtag('event', 'product_detail', {
      product_key: params.productId,
      plan_key: planKey,
      user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')?.id,
    });
  }, [productType, params.productId, planKey]);

  if (!data) return null;

  const [productDetail, planCmsDetail] = data;

  const insurer = planCmsDetail.cms?.data?.attributes?.insurer || {};
  const logoUrl =
    insurer?.data?.attributes?.logo?.data?.attributes?.url ||
    productDetail.details.logo_url;
  const planName =
    planCmsDetail.cms?.data?.attributes?.planName || productDetail?.name;
  let {
    benefits = [],
    coverages = [],
    enrolment = [],
  } = planCmsDetail?.cms?.data?.attributes;

  benefits = benefits?.map((e: any) => ({
    text: e?.text + (e?.coverage ? ': ' + e?.coverage : ''),
    id: e.id,
    subItems: e?.subItems?.map((item: any) => ({
      text: item?.text + (item?.coverage ? ': ' + item?.coverage : ''),
      id: item.id,
    })),
  }));

  const { note } = planCmsDetail?.cms?.data?.attributes;

  let segments = [
    {
      label: formatMessage({
        id: 'Benefits',
      }),
      value: benefits,
    },
    {
      label: formatMessage({
        id: 'Coverages',
      }),
      value: coverages,
    },
    {
      label: formatMessage({
        id: 'Enrolment',
      }),
      value: enrolment,
    },
  ].filter((e) => e.value?.length > 0);

  if (note) {
    segments.push({
      label: formatMessage({
        id: 'Note',
      }),
      value: [
        {
          id: '1',
          text: note,
        },
      ],
    });
  }

  return (
    <>
      <Spin spinning={loading}>
        <div
          className={classnames('planContainer', {
            'is-mobile': isMobile,
          })}
        >
          <PageFrame
            className="productDetailPageFrame"
            title={
              <div className={'titleWrap'}>
                <div className="titleSection">
                  <div className="titleLogoWrap">
                    <img className="titleLogo" src={logoUrl} />
                  </div>
                  <span className="titleName">{planName}</span>
                </div>
                {productType === 'health' && (
                  <span className="titleDesc">
                    {planName.includes('BSH')
                      ? formatMessage({
                          id: 'Available in 12 months coverage',
                        })
                      : formatMessage({
                          id: 'Available in 3, 6 and 12 months coverage',
                        })}
                  </span>
                )}
              </div>
            }
            onBack={() => {
              if (backTo) {
                history.replace(backTo);
              } else {
                history.goBack();
              }
            }}
          >
            <Row className="commissionInfo">
              <Col className="commissionInfoItem" span={isMobile ? 24 : 8}>
                <Space
                  size={4}
                  align="center"
                  className={'commissionInfoItemContainer'}
                >
                  <img src={images.commission_icon} />
                  <Space size={8} align="center">
                    <span className="commissionInfoItemText">
                      {formatMessage({ id: 'Commission' })}
                    </span>
                    <span className={'percent'}>
                      {numeral(planCmsDetail?.commission_rate).format(
                        '0.[00]%',
                      )}
                    </span>
                  </Space>
                </Space>
              </Col>
            </Row>

            <div className="productDetailMain">
              {segments.map((seg, segIndex) => {
                return (
                  <div
                    className="productDetailBox highlightsBox"
                    key={segIndex}
                  >
                    <div className="hightTitle">{seg.label}</div>
                    <ul className="segmentUl">
                      {seg.value?.map((section: any, sectionIndex: number) => {
                        const { text, subItems } = section;
                        if (!text || text?.trim() === '') return null;
                        return (
                          <li key={sectionIndex} className="segmentLi">
                            <span className="segment">{text}</span>
                            {subItems?.length > 0 && (
                              <div className="segmentSub">
                                {subItems?.map((sub: any, subIndex: number) => {
                                  if (sub?.text?.trim() === '') return null;
                                  return (
                                    <li key={subIndex}>
                                      <span className="segment">
                                        {sub?.text}
                                      </span>
                                    </li>
                                  );
                                })}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="productDetailFooter">
              <Button
                className="productDetailFooterButton"
                type="primary"
                onClick={() => {
                  sessionStorage.setItem(
                    SESSION_KEYS.PICKED_PLAN_INFO,
                    JSON.stringify({
                      planIcon: productDetail?.details.logo_url,
                      planKey: planKey,
                      planName: planCmsDetail?.cms?.data?.attributes?.planName,
                      productKey: productDetail?.product_key,
                      coverageType,
                      soiCategory,
                    }),
                  );
                  // 从fastquote的页面进来的
                  if (stillNeedCache) {
                    if (readyVehicleInfo) {
                      let vehicleInfoJSON = create(readyVehicleInfo);
                      if (productType === 'motor' && coverageType === 'tpl') {
                        vehicleInfoJSON =
                          deletePhsicalDamageParams(vehicleInfoJSON);
                      }
                      createQuote(
                        productType,
                        params.productId,
                        planKey,
                        vehicleInfoJSON,
                      );
                    } else {
                      let vehicleInfoJSON = create(readyVehicleInfo ?? {});
                      if (productType === 'motor' && coverageType === 'tpl') {
                        vehicleInfoJSON =
                          deletePhsicalDamageParams(vehicleInfoJSON);
                      }
                      createQuote(
                        productType,
                        params.productId,
                        planKey,
                        vehicleInfoJSON,
                      );
                    }
                  } else {
                    history.push({
                      pathname: `/home/${productType}/quote`,
                      state: {
                        from: 'product-list',
                      },
                      query: {
                        totalStep,
                      },
                    });
                  }

                  // event tracking: quote this product
                  window.gtag('event', 'quote_this_product', {
                    page: 'product_detail',
                    product_key: params.productId,
                    plan_key: planKey,
                    user_unique_id: JSON.parse(
                      localStorage.getItem('user_info') ?? '{}',
                    )?.id,
                  });
                }}
              >
                {formatMessage({ id: 'Get Quote' })}
              </Button>

              {totalStep !== '3' && (
                <Button
                  className="productDetailFooterButton"
                  style={{ marginLeft: 24 }}
                  type="primary"
                  onClick={() => {
                    sessionStorage.setItem(
                      SESSION_KEYS.PICKED_PLAN_INFO,
                      JSON.stringify({
                        planIcon: productDetail?.details.logo_url,
                        planKey: planKey,
                        planName:
                          planCmsDetail?.cms?.data?.attributes?.planName,
                        productKey: productDetail?.product_key,
                        coverageType,
                        soiCategory,
                      }),
                    );
                    // buy now flow
                    history.push({
                      pathname: `/home/${productType}/quote`,
                      state: {
                        from: 'product-list',
                      },
                      query: {
                        totalStep: '2',
                        button: 'buyNow',
                      },
                    });

                    // event tracking: quote this product
                    window.gtag('event', 'quote_this_product', {
                      page: 'product_detail',
                      product_key: params.productId,
                      plan_key: planKey,
                      user_unique_id: JSON.parse(
                        localStorage.getItem('user_info') ?? '{}',
                      )?.id,
                    });
                  }}
                >
                  {formatMessage({ id: 'Buy Now' })}
                </Button>
              )}
            </div>
          </PageFrame>
        </div>
      </Spin>
      {promptElement}
    </>
  );
};

export default ProductDetail;
